import { TZDate } from "@date-fns/tz";
import { differenceInMinutes, isBefore, set } from "date-fns";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";

// Flip to `true` to surface the maintenance banner on the sign-in screen.
// When `false`, the component renders nothing and skips the countdown work.
const MAINTENANCE_NOTICE_ENABLED = false;

// Maintenance window: 2:00 PM - 4:00 PM Mountain Standard Time.
// America/Phoenix is fixed MST (no DST).
const MST_TIME_ZONE = "America/Phoenix";
const MAINTENANCE_START_HOUR_MST = 14;
const MAINTENANCE_END_HOUR_MST = 16;

type MaintenancePhase = "before" | "during" | "after";
type MaintenanceCountdown = {
	phase: MaintenancePhase;
	hoursUntil: number;
	minutesUntil: number;
};

function computeMaintenanceCountdown(): MaintenanceCountdown {
	const now = new TZDate(Date.now(), MST_TIME_ZONE);
	const startInstant = set(now, {
		hours: MAINTENANCE_START_HOUR_MST,
		minutes: 0,
		seconds: 0,
		milliseconds: 0,
	});
	const endInstant = set(now, {
		hours: MAINTENANCE_END_HOUR_MST,
		minutes: 0,
		seconds: 0,
		milliseconds: 0,
	});

	let phase: MaintenancePhase;
	let diffMinutes: number;
	if (isBefore(now, startInstant)) {
		phase = "before";
		diffMinutes = differenceInMinutes(startInstant, now);
	} else if (isBefore(now, endInstant)) {
		phase = "during";
		diffMinutes = differenceInMinutes(endInstant, now);
	} else {
		phase = "after";
		diffMinutes = differenceInMinutes(now, endInstant);
	}

	const absMinutes = Math.abs(diffMinutes);

	return {
		phase,
		hoursUntil: Math.floor(absMinutes / 60),
		minutesUntil: absMinutes % 60,
	};
}

const MaintenanceNoticeContent = () => {
	// Computed once on mount via the lazy initializer; intentionally not
	// refreshed on a timer per product requirement.
	const [countdown] = useState(computeMaintenanceCountdown);

	return (
		<output
			aria-live="polite"
			className="block w-full max-w-[360px] mb-6 rounded-lg border border-amber-300 bg-amber-50 p-3 text-left text-amber-900"
			data-testid="maintenance-notice"
		>
			<div className="flex items-start gap-2">
				<AlertTriangle
					className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
					aria-hidden="true"
				/>
				<div className="flex-1">
					<p className="text-sm font-semibold">Scheduled maintenance</p>
					<p className="text-sm">
						{countdown.phase === "after" ? (
							<>
								Planned maintenance was scheduled for{" "}
								<span className="font-semibold">2:00 PM to 4:00 PM MST</span>.{" "}
								It may take a little longer to finish.
							</>
						) : (
							<>
								The site will be down between{" "}
								<span className="font-semibold">2:00 PM and 4:00 PM MST</span>.
							</>
						)}
					</p>
					<p className="mt-1 text-xs text-amber-800">
						{countdown.phase === "before" && (
							<>
								Starts in about{" "}
								<span className="font-semibold">
									{countdown.hoursUntil}h {countdown.minutesUntil}m
								</span>
								.
							</>
						)}
					</p>
				</div>
			</div>
		</output>
	);
};

const MaintenanceNotice = () => {
	if (!MAINTENANCE_NOTICE_ENABLED) return null;
	return <MaintenanceNoticeContent />;
};

export default MaintenanceNotice;
